import React, { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import { useModal } from 'react-modal-hook';
import classNames from 'classnames';

import {
	ProductsLayoutTypesBold,
	ProductsLayoutTypesClassic,
	ProductsLayoutTypesFun,
	TemplateClassNames,
} from '../../../../types/template-layouts';
import {
	ActionEvent,
	ActionTargetType,
	ActionType,
	ContentEditors,
	IProductAttributeLabel,
	ModuleMap,
	PageModule,
	PageModuleType,
	Product
} from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
import { EIcon } from '../../../general-ui/icon/icon';
import { ProductDetailsModal } from '../../containers/product-details-modal/product-details.modal';
import { ParamsProps } from '../../live-event';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../utils';
import ProductItem from './product-item';
import { getDefaultModuleAlignment, getTextValue } from '../../../../utils/utils';
import FilterDropdown from '../agenda/session-filter-dropdown';
import { useTracking } from '../../../../utils/tracking';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../../i18n/useTranslationModules';

import useTranslationRoute from '../../hooks/use-translation-route';

import '../../../../scss/live-event/base/modules/session-filter.scss';
import '../../../../scss/live-event/base/modules/products.scss';

interface IAttribute {
	type: string;
	value: string;
}

interface ProductsProps {
	module: PageModule;
	template: string;
	isLandingPage?: boolean;
}

const Products = React.forwardRef<HTMLDivElement, ProductsProps>(({ module, template, isLandingPage }, ref) => {
	const [product, setProduct] = useState<Product>();
	const [selectedCategory, setSelectedCategory] = useState('');
	const [selectedAttribute, setSelectedAttribute] = useState('');

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	const params: ParamsProps = useParams();
	const { trackEvent } = useTracking();

	const language = params.language || 'en';
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || ProductsLayoutTypesClassic.standard;
	//init as one variable so it can be passed to the card
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		titleOverrides,
		descriptionOverrides,
		dropdownOverrides,
		secondaryButtonOverrides,
	} = stylingOverrides;
	const [showMore, setShowMore] = useState(false);
	const { t: _t } = useTranslate(['homepage', 'landing']);
	const translationRoute = useTranslationRoute();

	const { t } = useTranslate(translationRoute);
	const [showProductDetailsModal, hideProductDetailsModal] = useModal(
		() => (
			<ProductDetailsModal
				template={template}
				layout_type={layout_type}
				onClose={hideProductDetailsModal}
				product={product as Product}
				stylingOverrides={stylingOverrides}
				language={language}
			/>
		),
		[product, template, layout_type, language]
	);

	const maxProducts = useMemo(() => {
		switch (layout_type) {
			case ProductsLayoutTypesClassic.standard:
			case ProductsLayoutTypesBold.standard:
			case ProductsLayoutTypesFun.standard:
				return 3;

			case ProductsLayoutTypesClassic.grid:
			case ProductsLayoutTypesBold.grid:
			case ProductsLayoutTypesFun.grid:
				return 4;

			case ProductsLayoutTypesClassic.vertical_list:
			case ProductsLayoutTypesBold.vertical_list:
			case ProductsLayoutTypesFun.vertical_list:
				return 4;

			default:
				return 2;
		}
	}, [layout_type]);

	const productsCount = modules?.length ?? maxProducts;

	const limitProductsIndex = useCallback(() => {
		if (showMore) return productsCount;
		else return maxProducts;
	}, [showMore, productsCount, maxProducts]);

	function handleShowMore() {
		setShowMore((prev) => !prev);
	}

	const { custom_heading, description, title } = getTextValue(content, language);

	const productCategories = useMemo(() => {
		if (!modules?.length) return [];
		const allCategories: string[] = [];
		modules?.forEach((module: Product) => {
			allCategories.push(...module.categories);
		});
		// convert to set and then back to array to ensure all strings are unique
		const uniqueCategories = Array.from(new Set(allCategories)).map((category: string) => ({
			label: t(`${translationRoute}:product.category.${category}`, category),
			value: category,
		}));

		uniqueCategories.sort((a: IProductAttributeLabel, b: IProductAttributeLabel) => {
			return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
		});
		return uniqueCategories;
	}, [modules, t, translationRoute]);

	// because product attributes are key/value pairs and the filter list is a single list,
	// we need to filter both the key and value to validate them together.
	const productAttributes = useMemo(() => {
		if (!modules?.length) return [];
		let allAttributes: { label: string, value: string }[] = [];
		const existsMap: { [key: string]: { [key: string]: string } } = {};
		modules?.forEach((module: Product) => {
			if (module?.attributes?.length) {
				module.attributes.forEach((attribute: IAttribute, index: number) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (key && value) {
						if (!existsMap?.[key]) {
							existsMap[key] = {};
						}
						if (!existsMap[key]?.[value]) {
							existsMap[key][value] = value;
							const label = t(`${translationRoute}:product.${module.module_id}.attribute.${index}.type`, key)
								+ " / "
								+ t(`${translationRoute}:product.${module.module_id}.attribute.${index}.value`, value);
							allAttributes.push({
								label: label,
								value: `${key} / ${value}`,
							});
						}
					}
				});
			}
		});
		allAttributes = allAttributes.sort((a: IProductAttributeLabel, b: IProductAttributeLabel) => {
			return a.label > b.label ? 1 : -1;
		});
		return allAttributes;
	}, [modules, t, translationRoute]);

	const handleAttributeChange = (value: string) => {
		setSelectedAttribute(value);
	};

	const handleCategoryChange = (value: string) => {
		setSelectedCategory(value);
	};

	const handleItemClick = (item: Product) => {
		setProduct(item);
		showProductDetailsModal();

		trackEvent({
			action: ActionEvent.Click,
			action_type: ActionType.Active,
			target_type: ActionTargetType.Product,
			target_id: product?.product
		});
	};

	const itemsToRender = useCallback(() => {
		if (!modules?.length) return [];
		let itemsToRender = [...modules];
		if (selectedCategory || selectedAttribute) {
			itemsToRender = itemsToRender.filter((module: Product) => {

				// if there's a selected category, then find it, otherwise set it to true
				const matchedCategory = selectedCategory ? module.categories.find(category => {
					return category?.toLowerCase().includes(selectedCategory.toLowerCase());
				}) : true;

				// if there's a selected attribute, then find it, otherwise set it to true
				const matchedAttribute = selectedAttribute ? module?.attributes?.find?.((attribute: IAttribute) => {
					const key = attribute.type?.trim()?.toLowerCase();
					const value = attribute.value?.trim()?.toLowerCase();
					if (`${key} / ${value}` === selectedAttribute) return true;
					return false;
				}) : true;

				// if both both category and attribute are true, then return the product
				return matchedCategory && matchedAttribute;
			});
		}
		return itemsToRender;
	}, [modules, selectedCategory, selectedAttribute]);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.products]}
			className={classNames(
				'products',
				`products-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<div className="products-text-wrapper">
					<TypographyItem className={classNames('evt-caption-1 products-custom-heading', customHeadingOverrides)} tagName="p">
						{t(`products.${module.id}.heading`, t('products.heading', custom_heading))}
					</TypographyItem>
					<div className="products-top">
						<div className="text-container">
							<TypographyItem className={classNames('evt-heading-2 products-title', titleOverrides)} tagName="h2">
								{t(`products.${module.id}.title`, t('products.title', title))}
							</TypographyItem>
							<TypographyItem className={classNames('evt-body-text-1 products-description')}>
								<RenderEditorDescription
									description={t(`products.${module.id}.description`, t('products.description', description))}
									descriptionOverrides={classNames(descriptionOverrides)}
									useNew={contentEditor === ContentEditors.quill}
									defaultTextAlignment={getDefaultModuleAlignment(module, template)}
								/>
							</TypographyItem>
						</div>
						<div className="dropdown-container">
							<FilterDropdown
								options={[{ label: _t('All attributes'), value: '' }, ...productAttributes]}
								onChange={(option) => {
									const value = option.value.toString();
									handleAttributeChange(value);
								}}
								template={template || TemplateClassNames.Apollo}
								value={selectedAttribute}
								className={dropdownOverrides}
								initialTitle={_t("All attributes")}
							/>
							<FilterDropdown
								options={[{ label: _t('All categories'), value: '' }, ...productCategories]}
								onChange={(option) => {
									const value = option.value.toString();
									handleCategoryChange(value);
								}}
								template={template || TemplateClassNames.Apollo}
								value={selectedCategory}
								className={dropdownOverrides}
								initialTitle={_t("All categories")}
							/>
						</div>
					</div>
				</div>
				<ul className="product-list">
					{itemsToRender()?.slice(0, limitProductsIndex()).map((module: Product) => (
						<ProductItem
							key={module.product}
							module={module}
							stylingOverrides={stylingOverrides}
							template={template}
							onClick={handleItemClick}
							isLandingPage={isLandingPage}
						/>
					))}
				</ul>
				{itemsToRender()?.length > maxProducts ? (
					<Button
						classButton={classNames(
							'products-more-button',
							'secondary',
							secondaryButtonOverrides
						)}
						template={template}
						onClick={handleShowMore}
					>
						<EIcon
							className="products-more-icon"
							name="refresh-transparent"
							position="order-left"
							size="medium"
						/>
						{_t("Show")} {showMore ? _t('Less') : _t('More')}
					</Button>
				) : null}
			</div>
		</section>
	);
});

Products.displayName = 'Products';

export default Products;
